// src/schedule/AddStaffPicker.jsx
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import * as Md from "react-icons/md";
import Avatar from "./Avatar";

/**
 * AddStaffPicker
 * - Portal (position: fixed) → overflow rodziców nie utnie panelu.
 * - AUTO-FLIP: gdy mało miejsca pod przyciskiem → panel otwiera się do GÓRY (używa CSS `bottom`).
 * - Przycisk zostaje po Twojemu (className z props), a panel/input/lista pobierają kolory ze zmiennych CSS (day/dark).
 */
const CONFLICT_LABELS = {
    unavailable: "niedostępny/a",
    assigned: "inne wydarzenie",
};

function normalizeTypedName(value) {
    return String(value || "").trim().replace(/\s+/g, " ");
}

export default function AddStaffPicker({
    employees = [],             // [{name, avatarUrl?}] lub [string] — tylko stała lista
    assignedNames = [],         // nazwiska już wpisane w obsadę tego wydarzenia
    onSelect,                   // (name: string) => void
    getConflictStatus,          // (name: string) => 'assigned' | 'unavailable' | null
    buttonLabel = "Dodaj",
    placeholder = "Szukaj…",
    width = 320,
    minHeight = 160,
    maxHeight = 420,
    // możesz nadpisać style przycisku; domyślnie pełna szerokość
    buttonClassName = "btn w-full flex items-center gap-2 justify-start text-left",
    // neutralna baza dla pozycji listy
    itemClassName = "flex items-center gap-3 px-3 py-2",
}) {
    const btnRef = useRef(null);
    const panelRef = useRef(null);

    const [open, setOpen] = useState(false);
    const [q, setQ] = useState("");

    const [layout, setLayout] = useState({
        left: 0,
        top: 0,          // używany gdy direction='down'
        bottom: 0,       // używany gdy direction='up'
        panelW: width,
        panelMaxH: maxHeight,
        direction: "down", // 'down' | 'up'
    });

    // Normalizacja listy
    const normalized = useMemo(
        () =>
            (employees || [])
                .map((e) => (typeof e === "string" ? { name: e } : e))
                .filter((e) => e?.name && e.isTemporary !== true && e.archived !== true),
        [employees]
    );

    const typedName = normalizeTypedName(q);
    const typedKey = typedName.toLowerCase();

    const filtered = useMemo(() => {
        if (!typedKey) return normalized;
        return normalized.filter((e) => e.name.toLowerCase().includes(typedKey));
    }, [normalized, typedKey]);

    const alreadyAssigned = useMemo(() => {
        if (!typedKey) return false;
        return (assignedNames || []).some(
            (name) => normalizeTypedName(name).toLowerCase() === typedKey
        );
    }, [assignedNames, typedKey]);

    const showAddCustom = Boolean(typedName) && filtered.length === 0 && !alreadyAssigned;

    const commitTypedName = () => {
        if (!showAddCustom) return;
        const name = typedName;
        setQ("");
        window.setTimeout(() => onSelect?.(name), 0);
    };

    // Pozycjonowanie + flip
    const positionPanel = useCallback(() => {
        const btn = btnRef.current;
        if (!btn) return;

        const gap = 8;
        const vw = window.innerWidth || document.documentElement.clientWidth || 0;
        const vh = window.innerHeight || document.documentElement.clientHeight || 0;
        const r = btn.getBoundingClientRect();

        const panelW = Math.min(Number(width) || 320, vw - 16);
        const left = Math.max(8, Math.min(r.left, vw - panelW - 8));

        const spaceBelow = Math.max(0, vh - r.bottom - gap - 8);
        const spaceAbove = Math.max(0, r.top - gap - 8);

        const maxDown = Math.max(minHeight, Math.min(maxHeight, spaceBelow));
        const maxUp = Math.max(minHeight, Math.min(maxHeight, spaceAbove));

        const direction = spaceBelow < minHeight && spaceAbove >= minHeight ? "up" : "down";

        if (direction === "down") {
            setLayout({
                left,
                top: r.bottom + gap,
                bottom: 0,
                panelW,
                panelMaxH: maxDown,
                direction: "down",
            });
        } else {
            setLayout({
                left,
                top: 0,
                bottom: Math.max(8, vh - r.top + gap),
                panelW,
                panelMaxH: maxUp,
                direction: "up",
            });
        }
    }, [width, minHeight, maxHeight]);

    useEffect(() => {
        if (!open) return;

        // pierwsze pozycjonowanie
        positionPanel();

        // repozycja przy scroll/resize
        const onScroll = () => positionPanel();
        const onResize = () => positionPanel();
        window.addEventListener("scroll", onScroll, true);
        window.addEventListener("resize", onResize);

        // zamykanie
        const onKey = (e) => e.key === "Escape" && setOpen(false);
        const onDown = (e) => {
            const panel = panelRef.current;
            const btn = btnRef.current;
            if (!panel || !btn) return;
            if (!panel.contains(e.target) && !btn.contains(e.target)) setOpen(false);
        };
        document.addEventListener("keydown", onKey);
        document.addEventListener("mousedown", onDown);

        // po zmianie zawartości (filtrowanie) panel może zmienić naturalną wysokość → przelicz
        const raf = requestAnimationFrame(positionPanel);
        const t = setTimeout(positionPanel, 0);

        return () => {
            window.removeEventListener("scroll", onScroll, true);
            window.removeEventListener("resize", onResize);
            document.removeEventListener("keydown", onKey);
            document.removeEventListener("mousedown", onDown);
            cancelAnimationFrame(raf);
            clearTimeout(t);
        };
    }, [open, q, filtered.length, showAddCustom, alreadyAssigned, width, minHeight, maxHeight, positionPanel]);

    return (
        <>
            {/* PRZYCISK – pełna szerokość + ikona; styl przycisku zostawiasz po swojemu (className z props) */}
            <button
                ref={btnRef}
                type="button"
                onClick={(e) => {
                    e.stopPropagation();
                    setOpen((v) => !v);
                    if (!open) setQ("");
                }}
                className={buttonClassName}
                title="Dodaj osobę"
                // dopinamy kolory z Twoich zmiennych (jeśli .btn ich nie nadaje)
                style={{
                    backgroundColor: "var(--btn-bg)",
                    color: "var(--btn-fg, var(--text))",
                    borderColor: "var(--btn-border, var(--border))",
                }}
            >
                <Md.MdPersonAdd size={18} />
                <span className="flex-1 truncate">{buttonLabel}</span>
            </button>

            {open &&
                createPortal(
                    <div
                        ref={panelRef}
                        className="fixed rounded-xl shadow-xl overflow-hidden border add-staff-picker-panel"
                        onClick={(e) => e.stopPropagation()}
                        onMouseDown={(e) => e.stopPropagation()}
                        style={{
                            left: layout.left,
                            width: layout.panelW,
                            ...(layout.direction === "down" ? { top: layout.top } : { bottom: layout.bottom }),
                            zIndex: 2147483648, // Wyższy niż powiększona karta (2147483647)
                            // KOLORY PANELU z Twoich zmiennych:
                            backgroundColor: "var(--panel-2)",
                            color: "var(--text)",
                            borderColor: "var(--border)",
                        }}
                    >
                        {/* Wyszukiwarka */}
                        <div
                            className="p-2 border-b"
                            style={{ borderColor: "var(--border)" }}
                        >
                            <input
                                value={q}
                                autoFocus
                                onChange={(e) => {
                                    e.stopPropagation();
                                    setQ(e.target.value);
                                }}
                                onKeyDown={(e) => {
                                    if (e.key !== "Enter") return;
                                    e.preventDefault();
                                    e.stopPropagation();
                                    commitTypedName();
                                }}
                                onClick={(e) => e.stopPropagation()}
                                onMouseDown={(e) => e.stopPropagation()}
                                placeholder={placeholder}
                                aria-label="Szukaj pracownika"
                                className="w-full px-3 py-2 rounded outline-none border"
                                style={{
                                    backgroundColor: "var(--btn-bg)",
                                    color: "var(--btn-fg, var(--text))",
                                    borderColor: "var(--btn-border, var(--border))",
                                }}
                            />
                        </div>

                        {/* Lista wyników (z Avatarem i ikoną) */}
                        <div className="overflow-auto" style={{ maxHeight: layout.panelMaxH }}>
                            {filtered.length === 0 && !showAddCustom && !alreadyAssigned && (
                                <div className="px-3 py-3 text-sm" style={{ color: "var(--muted)" }}>
                                    Brak wyników
                                </div>
                            )}
                            {alreadyAssigned && (
                                <div className="px-3 py-3 text-sm" style={{ color: "var(--muted)" }}>
                                    Już w obsadzie
                                </div>
                            )}
                            {showAddCustom && (
                                <button
                                    type="button"
                                    className="add-staff-picker-item w-full text-left px-3 py-2"
                                    onClick={(ev) => {
                                        ev.stopPropagation();
                                        commitTypedName();
                                    }}
                                >
                                    Dodaj "{typedName}"
                                </button>
                            )}

                            {filtered.map((e, idx) => {
                                const conflict = getConflictStatus?.(e.name) ?? null;
                                const conflictLabel = conflict ? CONFLICT_LABELS[conflict] : null;

                                return (
                                    <button
                                        key={`${e.name}-${idx}`}
                                        type="button"
                                        className={[
                                            itemClassName,
                                            "add-staff-picker-item w-full text-left",
                                            conflict ? "add-staff-picker-item--conflict" : "",
                                        ].filter(Boolean).join(" ")}
                                        onClick={(ev) => {
                                            ev.stopPropagation();
                                            const name = e.name;
                                            // Odroczenie — pozwala przeglądarce namalować klatkę przed ciężką pracą (INP).
                                            // Lista zostaje otwarta — multi-select aż do kliknięcia poza nią / Escape.
                                            window.setTimeout(() => onSelect?.(name), 0);
                                        }}
                                        title={
                                            conflictLabel
                                                ? `${e.name} — ${conflictLabel}`
                                                : e.name
                                        }
                                    >
                                        <Avatar employee={{ name: e.name, avatarUrl: e.avatarUrl }} size={24} title={e.name} />
                                        <span className="flex-1 min-w-0 truncate">{e.name}</span>
                                        {conflictLabel && (
                                            <span className="add-staff-picker-conflict-label shrink-0">
                                                {conflictLabel}
                                            </span>
                                        )}
                                        <Md.MdPersonAdd size={16} className="opacity-70 shrink-0" />
                                    </button>
                                );
                            })}
                        </div>
                    </div>,
                    document.body
                )}
        </>
    );
}
